import React, { useState, useEffect } from 'react'
import Footer from './Footer'
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import HeaderSchool from './HeaderSchool';
import PledgeModal from './SchoolAdmin/PledgeModal';
import EkPed3Modal from './SchoolAdmin/EkPed3Modal';
import { EKPED3_SCHOOL_ENABLED, SWACHHATA_SWM_REPORT_OPEN, EWASTE_DRIVE_OPEN } from '../config/campaigns';
import SwachhataSurveyModal from './SchoolAdmin/Swachhata/SwachhataSurveyModal';
import EWasteParticipationModal from './SchoolAdmin/EWasteParticipationModal';
import EWasteModal from './SchoolAdmin/EWasteModal';
import Sc6PromptModal from './SchoolAdmin/Sc6PromptModal';
import { isSc6Live, SC6_SCHOOL_PATH } from './SpecialCampaign6/sc6Config';
import BricsCampaignModal from './SchoolAdmin/BricsCampaign/BricsCampaignModal';
import { isBricsLive, BRICS_SCHOOL_PATH } from './SchoolAdmin/BricsCampaign/bricsConfig';
import { useSelector } from 'react-redux';
import { useCheckHealthyPledgeQuery, useCheckEkped3EventQuery, useGetSwachhataStatusQuery } from '../redux/auth/authApi';

export default function OutletRouteSchool() {
  const user = useSelector((state) => state.auth.user);
  const location = useLocation();
  const navigate = useNavigate();
  const isSchool = !!user && user.role === 2;
  const goToDashboard = () => {
    if (!location.pathname.includes('/school/schooldashboard') && location.pathname !== '/school') {
      navigate('/school/schooldashboard');
    }
  };
  const isPasswordPage = location.pathname.includes('/school/password');

  const { data: pledgeData, isFetching, refetch: refetchPledge } = useCheckHealthyPledgeQuery(undefined, {
    skip: !isSchool
  });
  const [showPledge, setShowPledge] = useState(false);

  // Ek Ped Maa Ke Naam 3.0 — post-login prompt. Auto-shown ONLY to schools with
  // no 3.0 event for the current campaign ("Have you participated?" → Yes: form,
  // No: encourage-to-participate). Schools that already have a record are never
  // auto-prompted; they update via the dashboard's "Update Ek Ped Maa Ke Naam 3.0
  // Details" button, which fires `openEkped3Modal` (handled below) and opens the
  // "Update the plantation count?" flow. UDISE + campaign resolve server-side.
  const {
    data: ekped3Data,
    isFetching: ekped3Fetching,
    refetch: refetchEkped3,
  } = useCheckEkped3EventQuery(undefined, { skip: !isSchool || !EKPED3_SCHOOL_ENABLED });
  // Dismissed for THIS session via "No"/close (redirects to Dashboard). The
  // auto-prompt therefore appears once per login/reload, not on every navigation.
  const [ekped3Dismissed, setEkped3Dismissed] = useState(false);
  // Opened explicitly from the dashboard button — bypasses the no-record gate.
  const [ekped3ManualOpen, setEkped3ManualOpen] = useState(false);

  useEffect(() => {
    const reopen = () => {
      setEkped3Dismissed(false);
      setEkped3ManualOpen(true);
    };
    window.addEventListener("openEkped3Modal", reopen);
    return () => window.removeEventListener("openEkped3Modal", reopen);
  }, []);

  // Swachhata Hi Seva 2026 — the school's Solid Waste Management report (form,
  // not a quiz). Auto-shown (once per login/reload) to every school that has not
  // filled it, after the Ek Ped 3.0 prompt is resolved so the two never stack.
  // The dashboard's "Fill Form" / "Update Details" button fires
  // `openSwachhataModal`, which opens straight into the (prefilled) form.
  const { data: swachhataData } = useGetSwachhataStatusQuery(undefined, { skip: !isSchool });
  const [swachhataDismissed, setSwachhataDismissed] = useState(false);
  const [swachhataManualOpen, setSwachhataManualOpen] = useState(false);

  useEffect(() => {
    const reopen = () => {
      setSwachhataDismissed(false);
      setSwachhataManualOpen(true);
    };
    window.addEventListener("openSwachhataModal", reopen);
    return () => window.removeEventListener("openSwachhataModal", reopen);
  }, []);

  const swachhataFilled = swachhataData?.participation?.survey?.status === "submitted";
  // Not gated on isFetching: saving the form refetches the status, and the popup
  // must stay mounted through that to show its thank-you step.
  const swachhataEligible = isSchool && swachhataData?.success === true;

  // E-Waste Collection Drive — school report. Its "filled or not" arrives on the
  // SAME status call (swachhataData.ewaste); nothing else is fetched on login.
  // Auto-shown (once per login/reload) only after the Swachhata Hi Seva popup
  // is resolved, while the school has not reported. The dashboard card fires
  // `openEWasteModal`, which opens straight into the form (the saved report is
  // fetched only then).
  const ewasteFilled = swachhataData?.ewaste?.status === "submitted";
  const [ewasteDismissed, setEwasteDismissed] = useState(false);
  const [ewasteManualOpen, setEwasteManualOpen] = useState(false);
  const [ewasteView, setEwasteView] = useState("gate"); // 'gate' | 'form' | 'done'
  const [ewasteCreated, setEwasteCreated] = useState(false);

  useEffect(() => {
    const open = () => {
      setEwasteDismissed(false);
      setEwasteManualOpen(true);
      setEwasteView("form");
    };
    window.addEventListener("openEWasteModal", open);
    return () => window.removeEventListener("openEWasteModal", open);
  }, []);

  // National Event on 28 Oct 2026 (Special Campaign 6) — its "filled or not"
  // also rides on the status call (swachhataData.sc6). While the event form is
  // live and the school has not uploaded, the prompt is shown first, once per
  // login; "Later" dismisses it for the session. Never on the form page itself.
  const sc6Filled = swachhataData?.sc6?.status === "submitted";
  const [sc6Dismissed, setSc6Dismissed] = useState(false);
  const onSc6Page = location.pathname.startsWith(SC6_SCHOOL_PATH);
  const showSc6 =
    isSc6Live() &&
    swachhataEligible &&
    !showPledge &&
    !isPasswordPage &&
    !onSc6Page &&
    !sc6Filled &&
    !sc6Dismissed;

  // BRICS Sustainable Lifestyle Week (1-7 Nov 2026). Its "filled or not" rides
  // on the same status call (swachhataData.brics), keyed by the PADDED UDISE —
  // a school that already sent either initiative is never prompted again. The
  // dashboard card reopens it for editing through "openBricsModal".
  // The prompt is about the 7-question School Reporting form, so it keeps
  // appearing for a school that only sent the two initiative forms.
  const bricsFilled = swachhataData?.brics?.weekReport === true;
  const [bricsDismissed, setBricsDismissed] = useState(false);
  const [bricsManualOpen, setBricsManualOpen] = useState(false);
  const [bricsInitialView, setBricsInitialView] = useState("announcement");
  const onBricsPage = location.pathname.startsWith(BRICS_SCHOOL_PATH);
  useEffect(() => {
    const reopen = (e) => {
      setBricsDismissed(false);
      setBricsManualOpen(true);
      setBricsInitialView(e?.detail?.view || "announcement");
    };
    window.addEventListener("openBricsModal", reopen);
    return () => window.removeEventListener("openBricsModal", reopen);
  }, []);
  const showBrics =
    isSchool &&
    !isPasswordPage &&
    (bricsManualOpen ||
      (isBricsLive() &&
        swachhataEligible &&
        !showPledge &&
        !showSc6 &&
        !onBricsPage &&
        !bricsFilled &&
        !bricsDismissed));
  const closeBrics = () => {
    setBricsManualOpen(false);
    setBricsDismissed(true);
  };

  const hasEkped3Record = (ekped3Data?.eventCount || 0) > 0;
  // Campaign switched off for schools → never eligible (see config/campaigns.js).
  const ekped3Eligible =
    EKPED3_SCHOOL_ENABLED && isSchool && !ekped3Fetching && ekped3Data?.success === true;

  // The pledge modal no longer auto-opens after login — it only opens when the
  // school clicks the pledge button on the dashboard (the "reopenPledgeModal"
  // event below).

  useEffect(() => {
    const openPledge = () => {
      const onPasswordPage = location.pathname.includes('/school/password');
      if (
        !isFetching &&
        isSchool &&
        pledgeData &&
        pledgeData.success &&
        pledgeData.isPledgeTaken === false &&
        !onPasswordPage
      ) {
        setShowPledge(true);
      }
    };

    window.addEventListener("reopenPledgeModal", openPledge);
    return () => {
      window.removeEventListener("reopenPledgeModal", openPledge);
    };
  }, [user, pledgeData, location.pathname, isFetching]);

  // Show the Ek Ped popup only after the pledge is resolved and never on the
  // password setup page. Auto-open is limited to schools with no record yet
  // (once per session); a record-holding school only sees it via the dashboard
  // button (manual open).
  const showEkped3 =
    ekped3Eligible &&
    !showPledge &&
    !showSc6 &&
    !showBrics &&
    !isPasswordPage &&
    (ekped3ManualOpen || (!hasEkped3Record && !ekped3Dismissed));
  // SWM report closed (config/campaigns.js) → never shown, and the E-Waste
  // popup below no longer waits for it.
  const showSwachhata =
    SWACHHATA_SWM_REPORT_OPEN &&
    swachhataEligible &&
    !showPledge &&
    !showSc6 &&
    !showBrics &&
    !showEkped3 &&
    !isPasswordPage &&
    (swachhataManualOpen || (!swachhataFilled && !swachhataDismissed));
  const swachhataResolved = !SWACHHATA_SWM_REPORT_OPEN || swachhataFilled || swachhataDismissed;
  // Never auto-open on top of the Swachhata certificate / students pages (the
  // school reached them from the Swachhata popup); it opens once they leave.
  const onSwachhataPage = /^\/school\/swachhata-/.test(location.pathname);
  const showEwaste =
    swachhataEligible &&
    !showPledge &&
    !showSc6 &&
    !showBrics &&
    !showEkped3 &&
    !showSwachhata &&
    !isPasswordPage &&
    // Drive closed (config/campaigns.js): never offer the form again.
    EWASTE_DRIVE_OPEN &&
    (ewasteManualOpen || (swachhataResolved && !ewasteFilled && !ewasteDismissed && !onSwachhataPage));
  const closeEwaste = () => {
    setEwasteDismissed(true);
    setEwasteManualOpen(false);
    setEwasteView("gate");
    goToDashboard();
  };
  const blocking = showPledge || showSc6 || showBrics || showEkped3 || showSwachhata || showEwaste;

  return (
    <>
      <HeaderSchool />
      <div style={{ position: 'relative', minHeight: '80vh' }}>
        <Sc6PromptModal isOpen={showSc6} onDismiss={() => setSc6Dismissed(true)} />
        <BricsCampaignModal
          isOpen={showBrics}
          initialView={bricsInitialView}
          onClose={closeBrics}
          onCompleted={() => setBricsManualOpen(false)}
        />
        <PledgeModal
          isOpen={showPledge}
          onClose={() => {
            setShowPledge(false);
          }}
        />
        {EKPED3_SCHOOL_ENABLED && (
        <EkPed3Modal
          isOpen={showEkped3}
          // `-count` has no `exists` flag — a school already has a 3.0 record when
          // it has logged at least one plantation event (eventCount > 0).
          exists={hasEkped3Record}
          total={ekped3Data?.trees || 0}
          cap={ekped3Data?.cap ?? 700}
          record={ekped3Data?.record || null}
          onClose={() => {
            // "No"/close: dismiss for the session and go to the dashboard.
            setEkped3Dismissed(true);
            setEkped3ManualOpen(false);
            goToDashboard();
          }}
          onCompleted={() => {
            // Event saved — re-check the total, then land on the dashboard.
            setEkped3Dismissed(true);
            setEkped3ManualOpen(false);
            refetchEkped3();
            goToDashboard();
          }}
        />
        )}
        {SWACHHATA_SWM_REPORT_OPEN && (
        <SwachhataSurveyModal
          isOpen={showSwachhata}
          // opened from the dashboard button → straight into the form
          direct={swachhataManualOpen}
          participation={swachhataData?.participation || null}
          onClose={() => {
            // "No"/close: dismiss for the session and go to the dashboard.
            setSwachhataDismissed(true);
            setSwachhataManualOpen(false);
            goToDashboard();
          }}
          // Saved: keep the popup open for the thank-you step (the auto-open
          // gate flips to "filled" as soon as the status refetches).
          onSaved={() => setSwachhataManualOpen(true)}
          onDone={() => {
            setSwachhataDismissed(true);
            setSwachhataManualOpen(false);
            goToDashboard();
          }}
          // Certificate is issued server-side with the first submission.
          onCertificate={() => {
            setSwachhataDismissed(true);
            setSwachhataManualOpen(false);
            navigate('/school/swachhata-certificate');
          }}
        />
        )}
        <EWasteParticipationModal
          isOpen={showEwaste && ewasteView !== "form"}
          mode={ewasteView === "done" ? "done" : "gate"}
          created={ewasteCreated}
          onFill={() => setEwasteView("form")}
          onDismiss={closeEwaste}
          onDone={closeEwaste}
        />
        <EWasteModal
          isOpen={showEwaste && ewasteView === "form"}
          exists={ewasteFilled}
          onClose={closeEwaste}
          // from the post-login gate the header arrow returns to the question
          onBack={ewasteManualOpen ? undefined : () => setEwasteView("gate")}
          onSaved={(res) => {
            setEwasteCreated(!!res?.created);
            // stay mounted for the thank-you step (the cache now says "filled")
            setEwasteManualOpen(true);
            setEwasteView("done");
          }}
        />
        {!blocking && <Outlet />}
        {blocking && (
          <div className="pledge-blocked-content" style={{ filter: 'blur(10px)', pointerEvents: 'none' }}>
            <Outlet />
          </div>
        )}
      </div>
      <Footer />
    </>
  )
}
